/**
 * Centralized API error handling for Apollo
 *
 * Extracts user-friendly error messages from Rocky API responses.
 * Rocky API returns errors in this format:
 * {
 *   reqID: string,
 *   errors: [{ message: string, code?: string, details?: any }]
 * }
 */

import { AxiosError } from "axios";

// Known Rocky API error codes and their user-friendly messages
const ERROR_CODE_MESSAGES: Record<string, string> = {
  // User errors
  INVALID_EMAIL: "Please enter a valid email address.",
  DUPLICATE_EMAIL: "This email is already in use by another account.",

  // Nutrition errors
  DUPLICATE_USER_GTIN: "A food with this barcode already exists.",

  // Workout session errors
  CAN_NOT_UPDATE: "This workout session can no longer be updated.",

  // Movement errors
  MOVEMENT_NOT_READY: "This movement video is still processing. Please wait.",

  // Asset errors
  endUsersAffected:
    "Cannot delete this asset because it is currently visible to users.",
};

// Fallback messages by HTTP status code
const STATUS_CODE_MESSAGES: Record<number, string> = {
  400: "Invalid request. Please check your input and try again.",
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "This action conflicts with existing data.",
  422: "The provided data is invalid.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Server error. Please try again later.",
  502: "Server temporarily unavailable. Please try again.",
  503: "Service temporarily unavailable. Please try again later.",
};

interface RockyAPIError {
  message?: string;
  code?: string;
  details?: unknown;
}

interface RockyErrorResponse {
  reqID?: string;
  errors?: RockyAPIError[];
}

/**
 * Extract a user-friendly error message from an API error
 *
 * @param error - The error caught from an API call (Axios error, Error, or unknown)
 * @param fallbackMessage - Default message if no specific error can be extracted
 * @returns A user-friendly error message
 *
 * @example
 * try {
 *   await apiClient.post('/updateTeam', data);
 * } catch (error) {
 *   toast.error(getApiErrorMessage(error, "Failed to update team"));
 * }
 */
export function getApiErrorMessage(
  error: unknown,
  fallbackMessage = "Something went wrong. Please try again."
): string {
  // Handle Axios errors with response data
  if (isAxiosError(error) && error.response) {
    const { status, data } = error.response;

    // Try to extract Rocky API error format
    if (isRockyErrorResponse(data)) {
      const rockyError = data.errors?.[0];

      if (rockyError) {
        // Check for known error code first
        if (rockyError.code && ERROR_CODE_MESSAGES[rockyError.code]) {
          return ERROR_CODE_MESSAGES[rockyError.code];
        }

        // Use the error message from the API if available
        if (rockyError.message) {
          return formatApiMessage(rockyError.message);
        }
      }
    }

    // Fall back to HTTP status code message
    if (STATUS_CODE_MESSAGES[status]) {
      return STATUS_CODE_MESSAGES[status];
    }

    // Generic status-based message
    if (status >= 500) {
      return "Server error. Please try again later.";
    }
    if (status >= 400) {
      return "Request failed. Please check your input and try again.";
    }
  }

  // Handle network errors (no response)
  if (isAxiosError(error) && !error.response) {
    if (error.code === "ECONNABORTED") {
      return "Request timed out. Please check your connection and try again.";
    }
    return "Network error. Please check your internet connection.";
  }

  // Handle standard Error objects
  if (error instanceof Error && error.message) {
    return formatApiMessage(error.message);
  }

  return fallbackMessage;
}

/**
 * Extract the error code from an API error (if available)
 * Useful for conditional handling based on error type
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (isAxiosError(error) && error.response) {
    const data = error.response.data;
    if (isRockyErrorResponse(data)) {
      return data.errors?.[0]?.code;
    }
  }
  return undefined;
}

export interface RockyErrorDetails {
  status?: number;
  codes?: string[];
  messages?: string[];
  reqID?: string;
}

/**
 * Pull the diagnosable parts of a failed Rocky call off an error: HTTP status,
 * every `errors[].code` / `errors[].message` (not just the first, which is all
 * `getApiErrorCode` exposes), and the `reqID` that locates the request in
 * Rocky's own logs. For error reporting — user-facing copy still comes from
 * `getApiErrorMessage`.
 *
 * Absent fields are left off rather than set to undefined, so the result can be
 * spread onto a Sentry scope without adding empty extras. A non-axios error, or
 * one with no response (network failure), yields `{}`.
 */
export function getRockyErrorDetails(error: unknown): RockyErrorDetails {
  if (!isAxiosError(error) || !error.response) return {};

  const { status, data } = error.response;
  const details: RockyErrorDetails = { status };

  if (isRockyErrorResponse(data)) {
    // An intercepting proxy can answer with a body of any shape, so never
    // assume `errors` is the array Rocky documents.
    const errors = Array.isArray(data.errors) ? data.errors : [];
    const codes = errors
      .map((e) => e?.code)
      .filter((code): code is string => !!code);
    const messages = errors
      .map((e) => e?.message)
      .filter((message): message is string => !!message);

    if (codes.length) details.codes = codes;
    if (messages.length) details.messages = messages;
    if (typeof data.reqID === "string") details.reqID = data.reqID;
  }

  return details;
}

/**
 * Get the HTTP status code from an error
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Check if an error is a specific Rocky API error code
 */
export function isApiErrorCode(error: unknown, code: string): boolean {
  return getApiErrorCode(error) === code;
}

// --- Token-refresh failure taxonomy -----------------------------------------
//
// The shared axios client (see `queries/constants.ts`) transparently refreshes
// the Rocky session token on a 401 and retries once. When that refresh itself
// fails the original 401 is re-rejected — but "refresh failed" hides two very
// different situations:
//
//   - "rejected": Rocky answered the refresh with a non-ok response (or gave us
//     no new token). The session is *genuinely expired*; retrying the request
//     will never succeed and the user must re-authenticate.
//   - "network": the refresh call never got a verdict (offline, timeout, or
//     missing config). The session may well still be valid, so this is a
//     transient failure worth retrying.
//   - "missing": there was no token to refresh WITH — the session cookie is
//     gone (browser purge, e.g. WebKit ITP's 7-day cap on script-set cookies)
//     and this tab holds no in-memory fallback. Refresh was never attempted.
//     The user must re-authenticate. Before this mode existed the interceptor
//     bailed silently here, which made the journal-save 401s of
//     LADDER-WEBSITE-8JN look like Rocky rejecting a valid token.
//
// The interceptor annotates the rejected error with which case it hit so
// consumers can react (e.g. prompt re-login only on real expiry) and so the
// otherwise-opaque 401 Sentry stream becomes diagnosable.

export type TokenRefreshFailureMode = "rejected" | "network" | "missing";

export interface TokenRefreshFailureMeta {
  mode: TokenRefreshFailureMode;
}

const TOKEN_REFRESH_FAILURE_KEY = "__tokenRefreshFailure";

/**
 * Additively tag a rejected error with the token-refresh failure mode. Safe to
 * call on any value; only mutates object-like errors.
 */
export function annotateTokenRefreshFailure(
  error: unknown,
  mode: TokenRefreshFailureMode
): void {
  if (error && typeof error === "object") {
    (error as Record<string, unknown>)[TOKEN_REFRESH_FAILURE_KEY] = { mode };
  }
}

/**
 * Read the token-refresh failure annotation off an error, if present.
 */
export function getTokenRefreshFailure(
  error: unknown
): TokenRefreshFailureMeta | undefined {
  if (
    error &&
    typeof error === "object" &&
    TOKEN_REFRESH_FAILURE_KEY in error
  ) {
    const meta = (error as Record<string, unknown>)[TOKEN_REFRESH_FAILURE_KEY];
    if (
      meta &&
      typeof meta === "object" &&
      (meta as TokenRefreshFailureMeta).mode
    ) {
      return meta as TokenRefreshFailureMeta;
    }
  }
  return undefined;
}

/**
 * True when a 401 is terminal and the user must log in again: either Rocky
 * actively rejected the refresh ("rejected"), or there was no token anywhere
 * to refresh with ("missing" — cookie purged and no in-memory fallback).
 * Both dead-end every retry, so both should route to re-auth. A transient
 * ("network") refresh failure returns false.
 */
export function isSessionExpiredError(error: unknown): boolean {
  const mode = getTokenRefreshFailure(error)?.mode;
  return mode === "rejected" || mode === "missing";
}

// Type guards

function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true
  );
}

function isRockyErrorResponse(data: unknown): data is RockyErrorResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    ("errors" in data || "reqID" in data)
  );
}

/**
 * Format an API error message for display
 * - Ensures message ends with proper punctuation
 * - Removes technical details that users don't need
 */
function formatApiMessage(message: string): string {
  let formatted = message.trim();

  // Remove common technical prefixes
  formatted = formatted.replace(/^Error:\s*/i, "");
  formatted = formatted.replace(/^API Error:\s*/i, "");

  // Ensure first letter is capitalized
  if (formatted.length > 0) {
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  // Add period if missing punctuation
  if (formatted && !/[.!?]$/.test(formatted)) {
    formatted += ".";
  }

  return formatted;
}
